import { Component, Injector, OnInit } from '@angular/core';
import { TableColumn } from '@models/tableColumn';
import { PricingsService } from '@services/entity/pricings.service';
import { StripeInfosService } from '@services/entity/stripe-infos.service';
import { plainToClass } from 'class-transformer';
import { AbstractEntitiesTableComponent } from '@abstracts/entities/entities-table';
import { PricingDto } from '@dto/pricingDto';
import { TableOptions } from '@models/tableOptions';
import { UsersService } from '@services/entity/users.service';
import { InvoiceDto } from '@dto/invoiceDto';
import { InvoicesService } from '@services/entity/invoices.service';
import { UserDto } from '@dto/userDto';
import { KwhPipe } from 'src/app/pipes/kwh.pipe';
import { DEDatePipe } from 'src/app/pipes/de-date.pipe';
import { CurrencyPipe } from '@angular/common';
import { CustomersService } from '@services/entity/customers.service';
import { BillingMethodEnum } from '@enums/billingMethodEnum.enum';
// import { HttpResponse } from '@angular/common/http';
// import { saveAs } from 'file-saver'

@Component({
  selector: 'app-invoices-table',
  templateUrl: './invoices-table.component.html',
  styleUrls: ['./invoices-table.component.scss'],
})
export class InvoicesTableComponent extends AbstractEntitiesTableComponent implements OnInit {

  public className = 'InvoicesTableComponent'

  public entities: InvoiceDto[] = []
  public hasStripeAccount: boolean
  public ownerUserId: string
  public ownerCustomerId: number
  public userId: string

  public isUser: boolean = false
  public isCustomer: boolean = false
  public isOperator: boolean = false

  public invoices: InvoiceDto[] = []
  public user: UserDto
  public users: UserDto[] = []
  public usersWithPricing: UserDto[] = []

  public tableColumns: TableColumn[] = [
    {
      columnDescription: `Tariftyp`, property: 'type', options:
        { tdStyleClasses: 'type' }
    },
    {
      columnDescription: `Ausstellungsdatum`, property: 'createdAt', options: {
        tdStyleClasses: 'createdAt', columnPipe: { pipe: new DEDatePipe() }, datePipeFormat: 'yyyy.MM.dd'
      }
    },
    {
      columnDescription: `Rechnungsnummer`, property: 'invoiceNumber', options: {
        tdStyleClasses: 'invoiceNumber'
      }
    },
    {
      columnDescription: `Summe kWh geladen`, property: 'invoiceMeterAmount', options: {
        tdStyleClasses: 'meterAmount', columnPipe: { pipe: new KwhPipe }
      }
    },
    {
      columnDescription: `Betrag`, property: 'invoiceAmount', options: {
        tdStyleClasses: 'amount', columnPipe: { pipe: new CurrencyPipe('de-DE', 'EUR') }
      }
    },
    {
      columnDescription: `Status`, property: 'statusName', options: {
        tdStyleClasses: 'status', isHTML: true
      }
    },
  ];

  public tableOptions: TableOptions = {
    hideActions: true,
    hideSearchAll: true,
    rowRouterLink: 'print/{id}'
  };

  constructor(
    public pricingsService: PricingsService,
    public stripeInfosService: StripeInfosService,
    public invoicesService: InvoicesService,
    public usersService: UsersService,
    public customersService: CustomersService,
    injector: Injector
  ) {
    super(pricingsService, injector)
  }

  ngOnInit(): void {
    this.getRoles()

    if (this.isOperator) {
      this.ownerCustomerId = this.activatedRoute.parent.snapshot.params.key;

      this.tableColumns.unshift(
        {
          columnDescription: `Benutzer`, property: 'recipientUserFullName', options: {
            tdStyleClasses: 'recipientUserFullName'
          }
        },
        {
          columnDescription: `Empfänger`, property: 'recipientFullName', options: {
            tdStyleClasses: 'recipientFullName'
          }
        })

      this.tableColumns.push({
        columnDescription: `-`, property: 'payoutStatementId', options: {
          tdStyleClasses: 'payout', isBoolean: true
        }
      })

      this.getUsers();

    } else if (this.isCustomer) {
      this.ownerCustomerId = localStorage.getItem('customerId') ? parseInt(localStorage.getItem('customerId')) : undefined

      this.tableColumns.unshift(
        {
          columnDescription: `Benutzer`, property: 'recipientUserFullName', options: {
            tdStyleClasses: 'recipientUserFullName'
          }
        },
        {
          columnDescription: `Empfänger`, property: 'recipientFullName', options: {
            tdStyleClasses: 'recipientFullName'
          }
        })
      this.tableColumns.push({
        columnDescription: `-`, property: 'payoutStatementId', options: {
          tdStyleClasses: 'payout', isBoolean: true
        }
      })

      this.getUsers();

    } else if (this.isUser) {
      this.userId = localStorage.getItem('userId')

      this.tableColumns.unshift(
        {
          columnDescription: `Aussteller`, property: 'ownerFullName', options: {
            tdStyleClasses: 'ownerFullName'
          }
        }
      )
    }

    this.getUser();
  }

  getUser() {
    this.usersService.getLoggedInUser().subscribe(user => {
      this.user = plainToClass(UserDto, user);
      super.ngOnInit();
    })
  }

  async initializeEntities() {
    if (this.isOperator) {
      this.customersService.getMasterUserOfCustomer(this.ownerCustomerId).subscribe(masterUser => {
        this.userId = masterUser.id;

        this.invoicesService.getInvoicesOfCustomer(this.userId).subscribe(invoices => {
          this.invoices = plainToClass(InvoiceDto, invoices)
          this.entities = this.invoices
          
          this.mapStatus()
          this.mapType()

          this.tableFunctions.sortColumn = 'createdAt'
          this.tableFunctions.sortDirection = 'desc'

          super.setupTable()
          this.tableReady = true

        }, error => console.error(error))
      })

    } else if (this.isCustomer) {
      this.invoicesService.getInvoicesOfCustomer(this.user.id).subscribe(invoices => {
        this.invoices = plainToClass(InvoiceDto, invoices)
        this.entities = this.invoices

        this.mapStatus()
        this.mapType()

        this.tableFunctions.sortColumn = 'createdAt'
        this.tableFunctions.sortDirection = 'desc'

        super.setupTable()
        this.tableReady = true
      }, error => console.error(error))

    } else if (this.isUser) {
      if (!this.userId) return false
      this.invoicesService.getInvoicesOfRecipient(this.userId).subscribe(invoices => {
        this.invoices = plainToClass(InvoiceDto, invoices)
        this.entities = this.invoices

        this.mapStatus()
        this.mapType()

        this.tableFunctions.sortColumn = 'createdAt'
        this.tableFunctions.sortDirection = 'desc'

        super.setupTable()
        this.tableReady = true
      }, error => console.error(error))
    }
  }

  mapStatus() {
    for (const entity of this.entities) {
      const status = this.getStatus(entity.status)
      entity['statusName'] = `<span class="invoice-status ${entity.status}">${status}<span>`
    }
  }

  mapType() {
    this.entities.forEach(entity => {
      if (entity.billingMethod == BillingMethodEnum.Manual) entity.type = 'Manuell'
      if (entity.billingMethod == BillingMethodEnum.QRCode) entity.type = 'QR-Code'
      if (entity.billingMethod == BillingMethodEnum.Automatic) entity.type = 'Automatisch'
    });
  }

  getStatus(status: string) {
    switch (status) {
      case 'Draft': return $localize`:@@Draft:Erstellt`;
      case 'Open': return $localize`:@@Open:Offen`;
      case 'Paid': return $localize`:@@Paid:Bezahlt`;
      case 'Cancelled': return $localize`:@@Cancelled:Storniert`;
      case 'Uncollectable': return $localize`:@@Uncollectable:Uneinbringbar`;
      default: return '';
    }
  }

  getRoles() {
    if (this.authenticationService.userHasRole('Operator')) this.isOperator = true;
    if (this.authenticationService.userHasRole('Customer')) this.isCustomer = true;
    if (this.authenticationService.userHasRole('User')) this.isUser = true;
  }

  async getUsers() {
    if (this.isOperator) {
      this.usersService.getUsersOfCustomerAsync(this.ownerCustomerId).then(users => {
        this.users = plainToClass(UserDto, users)

        for (const user of this.users) {
          if (user.pricingId != null) {
            this.usersWithPricing.push(user)
          }
        }
      });
    } else {
      this.usersService.getUsersOfCurrentCustomerAsync().then(users => {
        this.users = plainToClass(UserDto, users)

        for (const user of this.users) {
          if (user.pricingId != null) {
            this.usersWithPricing.push(user)
          }
        }
      });
    }
  }

  matches(entity: InvoiceDto, term: string) {
    return InvoicesService.matches(entity, term)
  }
}

